import { useEventListener } from 'ahooks'
import styles from './VideoController.module.less'
import cls from 'classnames'
import { useEffect, useRef, useState } from 'react'
import Player from 'xgplayer/es/player'
import { DASHBOARD_CONTENT_CONTAINER_ID } from '@/router/pageConst'
import { useChartConfig } from '../CoreChart/CoreChart'
import AIcon from '@/components/A/AIcon/AIcon'
import VideoVolumeBar from '../VideoVolumeBar/VideoVolumeBar'
import VideoSecTime from '../VideoSecTime/VideoSecTime'
import { Popover } from 'antd'
import MiddleText from '@/components/MiddleText/MiddleText'
import { useTranslation } from 'react-i18next'


interface Props {
  id: string
  className?: string
  videoInstance: Player | null
  videoSize: { width: string, height: string, left: string, top: string }
  duration: number
  axisCount?: number
  shouldRefresh: boolean
  progress?: number
  disableProgressTransition?: boolean
  onProgressChange?: (progress: number | undefined) => void
  onDragTimeChange?: (time: number) => void
  onDragStart: () => void
  onDragEnd: () => void
  onControllerMouseMove?: () => void
  onControllerMouseLeave?: () => void
  onVolumeChange?: (rate: number) => void
  onRefresh: () => void
  needPause: () => void
  needPlay: () => void
  // project: ProjectModels.ProjectDetail
  detailMetrics: ProjectModels.DetailMetricItem[]
}

const rangedProgress = (progress: number) => {
  if (progress >= 100) {
    return 100
  }
  if (progress <= 0) {
    return 0
  }
  return progress
}

const BAR_HEIGHT = 46

function VideoController(props: Props) {
  const { t } = useTranslation()
  const { config } = useChartConfig(props.detailMetrics)
  const chartGrid = config.grid as any
  const { left, } = props.videoSize
  const videoWidth = Number(props.videoSize.width.replace('px', ''))
  const safeAxisCount = Math.max(1, props.axisCount || props.duration || 1)
  const xAxisWidth = videoWidth / safeAxisCount

  const [isPlaying, setIsPlaying] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [isHover, setIsHover] = useState(false)
  const lineDotBoxRef = useRef<HTMLDivElement>(null)
  const videoControllerRef = useRef<HTMLDivElement>(null)
  const [isRefreshPopoverOpen, setIsRefreshPopoverOpen] = useState(true)
  const rafIdRef = useRef<number | undefined>(undefined)
  const isPlayingRef = useRef(false)
  const progressTransform = props.progress !== undefined
    ? `translateX(${-100 + props.progress}%)`
    : 'translateX(-100%)'


  const updateProgress = (nextProgress: number | undefined) => {
    props.onProgressChange?.(nextProgress)
  }

  const stopProgressLoop = () => {
    if (rafIdRef.current !== undefined) {
      window.cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = undefined
    }
  }

  const startProgressLoop = () => {
    stopProgressLoop()
    const tick = () => {
      if (!props.videoInstance || !isPlayingRef.current) {
        return
      }
      const currentTime = props.videoInstance.currentTime || 0
      const nextProgress = rangedProgress(currentTime / props.duration * 100)
      updateProgress(nextProgress)
      rafIdRef.current = window.requestAnimationFrame(tick)
    }
    rafIdRef.current = window.requestAnimationFrame(tick)
  }

  const handleBbClick = () => {
    if (props.videoInstance) {
      if (isPlaying) {
        props.needPause()
        doPause()
      } else {
        props.needPlay()
        doPlaying()
      }
    }
  }

  const handleBbRefresh = () => {
    props.onRefresh()
    /** 重置状态 */
    props.onDragTimeChange?.(0)
    doPause()
    updateProgress(undefined)
    setIsMoving(false)
    setIsHover(false)
  }

  const doPlaying = () => {
    setIsPlaying(true)
    isPlayingRef.current = true
    startProgressLoop()
  }

  const doPause = () => {
    setIsPlaying(false)
    isPlayingRef.current = false
    stopProgressLoop()
  }

  const handleLineDotBoxMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    props.needPause()
    doPause()
    const { left, width } = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - left
    const progress = x / width * 100
    const currentTime = progress * props.duration / 100
    props.onDragTimeChange?.(currentTime)
    updateProgress(rangedProgress(progress))
    setIsMoving(true)
  }

  const handleLineDotBoxMouseMove = (e: MouseEvent) => {
    if (!isMoving) return
    const { left, width } = lineDotBoxRef.current!.getBoundingClientRect()
    const x = e.clientX - left
    const progress = x / width * 100
    const currentTime = progress * props.duration / 100
    props.onDragTimeChange?.(currentTime)
    updateProgress(rangedProgress(progress))
  }

  const handleCloseRefreshPopover = () => {
    setIsRefreshPopoverOpen(false)
  }

  useEventListener('mousemove', (e) => {
    e.preventDefault()
    props.onControllerMouseMove?.()
    setIsHover(true)
  }, { target: videoControllerRef.current })

  useEventListener('mouseleave', () => {
    props.onControllerMouseLeave?.()
    setIsHover(false)
  }, { target: videoControllerRef.current })

  useEventListener('mousedown', (e) => {
    handleLineDotBoxMouseDown(e)
  }, { target: lineDotBoxRef.current })

  useEventListener('mousemove', (e) => {
    e.preventDefault()
    e.stopPropagation()
    handleLineDotBoxMouseMove(e)
  }, { target: document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID) as HTMLDivElement })

  useEventListener('mouseup', (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsMoving(false)
  }, { target: document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID) as HTMLDivElement })

  useEffect(() => {
    if (isMoving) {
      props.onDragStart()
    } else {
      props.onDragEnd()
    }
  }, [isMoving])

  useEffect(() => {
    if (props.videoInstance) {
      props.videoInstance.on('play', () => {
        doPlaying()
      })
      props.videoInstance.on('pause', () => {
        const currentTime = props.videoInstance!.currentTime || 0
        const progress = currentTime / props.duration * 100
        updateProgress(rangedProgress(progress))
        doPause()
      })
      props.videoInstance.on('ended', () => {
        setTimeout(() => {
          updateProgress(undefined)
          doPause()
        }, 300)
      })
    }
  }, [props.videoInstance])

  useEffect(() => {
    return () => {
      stopProgressLoop()
    }
  }, [])


  return <>
    <div id={props.id} className={cls(styles.videoController, props.className)}
      style={{ width: videoWidth, left, bottom: 0, height: BAR_HEIGHT + chartGrid.bottom }}
      ref={videoControllerRef}>
      <div className={styles.videoControllerContent} style={{ width: videoWidth - xAxisWidth, marginLeft: xAxisWidth * 0.5 }}>
        <div className={cls(
          styles.lineDotBox,
          isMoving && styles.isMoving,
          isHover && styles.isHover,
          props.disableProgressTransition && styles.disableProgressTransition
        )} style={{ width: videoWidth - xAxisWidth }} ref={lineDotBoxRef} >
          <div className={styles.line} >
            <div className={styles.progress} style={{ transform: progressTransform }}></div>
          </div>
          <div className={styles.dotTrack} style={{ transform: progressTransform }}>
            <div className={styles.dot}></div>
          </div>
        </div>
        <div className={styles.bar} style={{ height: BAR_HEIGHT, marginLeft: -BAR_HEIGHT * 0.1, }}>
          <div className={styles.barBox}>
            {
              props.shouldRefresh
                ? <div className={styles.barPlay} style={{ width: BAR_HEIGHT * 0.5, height: BAR_HEIGHT * 0.5 }} >
                  <Popover
                    getPopupContainer={() => videoControllerRef.current!}
                    placement='topLeft'
                    defaultOpen={true}
                    open={isRefreshPopoverOpen}
                    zIndex={1}
                    autoAdjustOverflow={false}
                    arrow={{ pointAtCenter: true }}
                    content={
                      <div className={styles.refreshPopover}>
                        <span className={styles.refreshCloseIcon} onClick={handleCloseRefreshPopover}>
                          <AIcon width={16} height={16} type="icon-line-global_close1"></AIcon>
                        </span>
                        <div className={styles.refreshTitle}>
                          <AIcon width={22} height={22} type="icon-fill-global_alert"></AIcon>
                          <MiddleText text={t('dashboard.projectDetailPage.videoView.videoChart.videoFailed')}></MiddleText>
                        </div>
                        <div className={styles.refreshDesc}>{t('dashboard.projectDetailPage.videoView.videoChart.videoFailedAction')}</div>
                      </div>
                    }>
                    <span onClick={handleBbRefresh}>
                      <AIcon className={styles.barPlayRefreshIcon} width={18} height={18} type='icon-fill-global_refresh' />
                    </span>
                  </Popover>
                </div>
                : <div className={styles.barPlay} style={{ width: BAR_HEIGHT * 0.5, height: BAR_HEIGHT * 0.5 }} onClick={handleBbClick}>
                  <AIcon className={styles.barPlayIcon} type={isPlaying ? 'icon-fill-global_play3' : 'icon-fill-global_play2'} />
                </div>
            }
            <VideoSecTime progress={props.progress} duration={props.duration} />
          </div>
          <VideoVolumeBar barHeight={BAR_HEIGHT} onVolumeChange={props.onVolumeChange} />
        </div>
      </div>
    </div>
  </>
}



export default VideoController
